import { Box, Typography, IconButton } from "@mui/material";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { IAdvisor, ITouchpoint, IViewpoint } from "../../../types";
import { useAdvisor } from "../context/AdvisorContext";
import ClientProvider from "../../../modules/ClientProvider";
import { X } from "lucide-react";
import Loading from "../../../components/Loading";

export const AdvisorSurveyFullscreen = () => {
    const { touchpointUuid } = useParams();
    const navigate = useNavigate();
    const { advisor, loading } = useAdvisor();
    const { getAccessTokenSilently } = useAuth0();
    
    const [touchpoint, setTouchpoint] = useState<ITouchpoint | null>(null);
    const [touchpointLoading, setTouchpointLoading] = useState(false);
    const [surveyCompleted, setSurveyCompleted] = useState(false);

    const fetchTouchpoint = async () => {
        if (!touchpointUuid) {
            return;
        }
        setTouchpointLoading(true);
        const accessToken = await getAccessTokenSilently();
        const touchpointClient = ClientProvider.provideAdvisorTouchpointClient();
        const { data, error } = await touchpointClient.getAdvisorTouchpoint(advisor?.survey_link?.project?.tag || '', touchpointUuid, accessToken);
        if (data?.touchpoint) {
            setTouchpoint(data.touchpoint);
        }
        setTouchpointLoading(false);
    }

    useEffect(() => {
        if (advisor && touchpointUuid) {
            fetchTouchpoint();
        }
    }, [advisor, touchpointUuid]);

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.data && typeof event.data === 'object' && event.data.type === 'status') {
                if (event.data.status === 'completed') {
                    setSurveyCompleted(true);
                    // Navigate back to the main feedback page after completion
                    setTimeout(() => {
                        navigate(`/advisor/feedback/${touchpointUuid}`);
                    }, 2000);
                }
            }
        };

        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [touchpointUuid, navigate]);

    if (loading || touchpointLoading || !advisor || !touchpoint) {
        return <Loading />;
    }

    const viewpoint = touchpoint?.viewpoints?.[0];
    const url = viewpoint?.alchemer_survey_url || `${touchpoint.default_survey_url}?blid=${advisor.public_id}&tpid=${touchpoint.uuid}`;

    return (
        <Box sx={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#ffffff',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* Header */}
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                p: 2,
                borderBottom: '1px solid #e0e0e0',
                backgroundColor: '#f8f9fa',
                minHeight: '60px'
            }}>
                <Typography variant="h6">{touchpoint.name}</Typography>
                <IconButton 
                    onClick={() => navigate(`/advisor/feedback/${touchpointUuid}`)}
                    sx={{ color: '#666' }}
                >
                    <X size={24} />
                </IconButton>
            </Box>
            
            {/* Survey completion message */}
            {surveyCompleted && (
                <Box sx={{ 
                    p: 3, 
                    textAlign: 'center',
                    backgroundColor: '#f0f8f0',
                    borderBottom: '1px solid #d0e0d0'
                }}>
                    <Typography variant="h6" color="success.main">
                        Survey completed! Redirecting...
                    </Typography>
                </Box>
            )}
            
            {/* Full-screen iframe */}
            <Box sx={{ 
                flex: 1, 
                width: '100%',
                position: 'relative'
            }}>
                <iframe
                    src={url}
                    frameBorder="0"
                    width="100%"
                    height="100%"
                    allow="microphone *; camera *"
                    style={{
                        border: "none",
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0
                    }}
                />
            </Box>
        </Box>
    );
}; 